import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import {
  Bell, BookOpen, Building2, Check, ChevronDown, CircleDollarSign, Coins, Home, KeyRound, LineChart, LogOut, Menu, PanelLeft,
  Radar, Search, Settings, Shield, TrendingUp, Wrench, X,
} from "lucide-react";
import { gtmOrg } from "../../lib/gtm";
import { api } from "../../lib/api";
import { formatMoney } from "../../lib/format";
import { useInterval } from "../../lib/hooks";
import { DropdownMenu } from "radix-ui";
import { Logo } from "./Logo";
import { CommandPalette, type Command, Skeleton, Button, ErrorNote } from "../ui";
import { useSession } from "../../lib/session";
import { cn } from "../../lib/cn";
import { CreateOrgCard } from "../../pages/provider/CreateOrg";

interface NavItem { to: string; label: string; icon?: ReactNode; end?: boolean }
/** A top-level link, or a collapsible group whose children render indented without icons. */
type NavEntry = NavItem | { group: string; icon: ReactNode; items: NavItem[] };
const isGroup = (e: NavEntry): e is Extract<NavEntry, { group: string }> => "group" in e;

const ic = "size-[18px] stroke-[1.6]";
export const PROVIDER_NAV: NavEntry[] = [
  // Searching the connected databases is the main thing the app does, so it comes first.
  { to: "/ask", label: "Search", icon: <Search className={ic} /> },
  { to: "/dashboard", label: "Dashboard", icon: <Home className={ic} />, end: true },
  { to: "/developers/api-keys", label: "API keys", icon: <KeyRound className={ic} /> },
  {
    group: "Build", icon: <Wrench className={ic} />, items: [
      { to: "/sources", label: "Data sources" },
      { to: "/datasets", label: "Datasets" },
      { to: "/architect", label: "AI Data Architect" },
      { to: "/sync", label: "Sync" },
    ],
  },
  {
    group: "Grow", icon: <TrendingUp className={ic} />, items: [
      { to: "/opportunities", label: "AI demand" },
      { to: "/discovery", label: "Discovery" },
      { to: "/queries", label: "Queries" },
      { to: "/consumers", label: "Consumers" },
    ],
  },
  {
    group: "Earnings", icon: <CircleDollarSign className={ic} />, items: [
      { to: "/revenue", label: "Revenue" },
      { to: "/costs", label: "Costs" },
      { to: "/pricing/manage", label: "Pricing" },
    ],
  },
  {
    group: "Manage", icon: <Shield className={ic} />, items: [
      { to: "/security/center", label: "Security" },
      { to: "/audit", label: "Audit logs" },
      { to: "/settings/team", label: "Team" },
      { to: "/settings", label: "Settings", end: true },
    ],
  },
];

export const CONSUMER_NAV: NavEntry[] = [
  { to: "/consumer", label: "Dashboard", icon: <Home className={ic} />, end: true },
  { to: "/consumer/api-keys", label: "API keys", icon: <KeyRound className={ic} /> },
  {
    group: "Build", icon: <Wrench className={ic} />, items: [
      { to: "/consumer/discover", label: "Discover" },
      { to: "/consumer/integrations", label: "Integrations" },
      { to: "/consumer/waitlists", label: "Waitlists" },
    ],
  },
  {
    group: "Usage", icon: <LineChart className={ic} />, items: [
      { to: "/consumer/usage", label: "Usage" },
      { to: "/consumer/costs", label: "Costs" },
      { to: "/consumer/logs", label: "Query logs" },
    ],
  },
  {
    group: "Manage", icon: <Settings className={ic} />, items: [
      { to: "/consumer/billing", label: "Billing" },
      { to: "/consumer/budgets", label: "Budgets & limits" },
      { to: "/consumer/team", label: "Team" },
    ],
  },
];

const flat = (entries: NavEntry[]) => entries.flatMap((e) => (isGroup(e) ? e.items : [e]));

// Per-viewer UI conveniences only; storage may be unavailable, so every access is guarded.
function stored<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v === null ? fallback : (JSON.parse(v) as T); } catch { return fallback; }
}
function store(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
}

export function AppShell({ mode }: { mode: "provider" | "consumer" }) {
  const { me, loading, offline, membership, setOrgId, refresh } = useSession();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => stored("dbx.sidebar.collapsed", false));
  useEffect(() => store("dbx.sidebar.collapsed", collapsed), [collapsed]);
  useEffect(() => setMobileOpen(false), [loc.pathname]);

  const commands = useMemo<Command[]>(() => {
    const c: Command[] = [
      ...flat(PROVIDER_NAV).map((n) => ({ id: "p" + n.to, label: n.label, group: "Provider", to: n.to })),
      ...flat(CONSUMER_NAV).map((n) => ({ id: "c" + n.to, label: n.label, group: "Consumer", to: n.to })),
      { id: "new-source", label: "Connect a new data source", group: "Action", to: "/sources/new", keywords: "add database postgres mysql" },
      { id: "new-key", label: "Create API key", group: "Action", to: "/consumer/api-keys", keywords: "token secret" },
      { id: "mcp", label: "MCP server setup", group: "Developers", to: "/developers/mcp" },
      { id: "docs", label: "API documentation", group: "Developers", to: "/docs" },
      { id: "market", label: "Browse marketplace", group: "Public", to: "/marketplace" },
      { id: "wanted", label: "Data wanted by AI", group: "Public", to: "/data-wanted" },
      ...(me?.platformAdmin ? [{ id: "internal", label: "Command centre", group: "Internal", to: "/internal" }] : []),
    ];
    for (const m of me?.memberships ?? []) c.push({ id: "org-" + m.orgId, label: `Switch to ${m.orgName}`, group: "Organisation", run: () => setOrgId(m.orgId) });
    return c;
  }, [me, setOrgId]);

  if (loading)
    return (
      <div className="flex min-h-screen">
        <div className="w-64 border-r border-border p-4">
          <Skeleton className="h-6 w-28" />
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="mb-4 h-7 w-56" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  if (offline)
    return (
      <div className="mx-auto max-w-lg p-10">
        <Logo />
        <ErrorNote className="mt-6" error={{ message: "The DBIndex control plane is unreachable. Check that the API is running (pnpm dev:workers) and retry." }} />
        <Button className="mt-4" onClick={() => void refresh()}>
          Retry
        </Button>
      </div>
    );
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (!membership)
    return (
      <div className="mx-auto max-w-lg p-10">
        <Logo />
        <CreateOrgCard />
      </div>
    );

  const sidebar = (compact: boolean, onClose?: () => void) => (
    <Sidebar
      mode={mode}
      compact={compact}
      orgId={membership.orgId}
      onSearch={() => setPaletteOpen(true)}
      onToggle={onClose ?? (() => setCollapsed((c) => !c))}
      toggleLabel={onClose ? "Close navigation" : compact ? "Expand sidebar" : "Collapse sidebar"}
      toggleIcon={onClose ? <X className="size-5" /> : undefined}
    />
  );

  return (
    <div className="flex min-h-screen">
      <aside className={cn("sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border bg-bg-elev transition-[width] duration-150 lg:flex", collapsed ? "w-[68px]" : "w-64")}>
        {sidebar(collapsed)}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="flex w-72 flex-col border-r border-border bg-bg-elev">{sidebar(false, () => setMobileOpen(false))}</div>
          <div className="flex-1 bg-black/50" onClick={() => setMobileOpen(false)} />
        </div>
      )}
      <CommandPalette commands={commands} open={paletteOpen} onOpenChange={setPaletteOpen} trigger={false} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-bg/90 px-4 backdrop-blur lg:hidden">
          <button className="rounded p-1.5 text-fg-2" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
            <Menu className="size-5" />
          </button>
          <Logo to={mode === "provider" ? "/ask" : "/consumer"} />
          <div className="ml-auto flex items-center gap-1">
            <button className="rounded-md p-1.5 text-fg-2 hover:bg-panel-2 hover:text-fg" aria-label="Search" onClick={() => setPaletteOpen(true)}>
              <Search className="size-4" />
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const rowBase = "flex h-9 items-center gap-3 rounded-lg px-2.5 text-[14px] transition-colors";
const rowIdle = "text-fg-2 hover:bg-panel-2/70 hover:text-fg";
const rowActive = "bg-panel-2 font-medium text-fg";

function Sidebar({ mode, compact, orgId, onSearch, onToggle, toggleLabel, toggleIcon }: {
  mode: "provider" | "consumer"; compact: boolean; orgId: string;
  onSearch: () => void; onToggle: () => void; toggleLabel: string; toggleIcon?: ReactNode;
}) {
  const { me } = useSession();
  const loc = useLocation();
  const entries = mode === "provider" ? PROVIDER_NAV : CONSUMER_NAV;
  const [closedGroups, setClosedGroups] = useState<string[]>(() => stored(`dbx.sidebar.closed.${mode}`, []));
  useEffect(() => store(`dbx.sidebar.closed.${mode}`, closedGroups), [closedGroups, mode]);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const matches = (n: NavItem) => (n.end ? loc.pathname === n.to : loc.pathname === n.to || loc.pathname.startsWith(n.to + "/"));

  return (
    <>
      <div className={cn("flex items-center pt-4", compact ? "flex-col gap-3 px-2" : "justify-between px-4")}>
        <Logo to={mode === "provider" ? "/ask" : "/consumer"} compact={compact} />
        <button onClick={onToggle} aria-label={toggleLabel} title={toggleLabel} className="rounded-md p-1.5 text-fg-3 hover:bg-panel-2 hover:text-fg">
          {toggleIcon ?? <PanelLeft className="size-[18px] stroke-[1.6]" />}
        </button>
      </div>

      <div className={cn("space-y-2 pt-4", compact ? "px-2" : "px-3")}>
        <WorkspaceSwitch mode={mode} compact={compact} />
        {compact ? (
          <button onClick={onSearch} aria-label="Search" title="Search (⌘K)" className="flex h-10 w-full items-center justify-center rounded-lg border border-border bg-panel text-fg-3 hover:border-border-strong hover:text-fg">
            <Search className="size-[18px] stroke-[1.6]" />
          </button>
        ) : (
          <button onClick={onSearch} className="flex h-10 w-full items-center gap-2.5 rounded-lg border border-border bg-panel px-3 text-[14px] text-fg-3 hover:border-border-strong">
            <Search className="size-[18px] stroke-[1.6]" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-left">Search DBIndex…</span>
            <kbd className="flex shrink-0 gap-0.5 font-sans text-[11px]">
              <span className="rounded border border-border px-1 py-px">{isMac ? "⌘" : "Ctrl"}</span>
              <span className="rounded border border-border px-1 py-px">K</span>
            </kbd>
          </button>
        )}
      </div>

      <nav aria-label={mode === "provider" ? "Provider navigation" : "Consumer navigation"} className={cn("flex-1 overflow-y-auto py-4", compact ? "px-2" : "px-3")}>
        <ul className="space-y-0.5">
          {entries.map((e) => {
            if (!isGroup(e))
              return (
                <li key={e.to}>
                  <NavLink to={e.to} end={e.end} title={compact ? e.label : undefined} className={({ isActive }) => cn(rowBase, compact && "justify-center px-0", isActive ? rowActive : rowIdle)}>
                    <span className="shrink-0 text-fg-3" aria-hidden>{e.icon}</span>
                    {!compact && e.label}
                  </NavLink>
                </li>
              );
            const active = e.items.some(matches);
            if (compact)
              return (
                <li key={e.group}>
                  <NavLink to={e.items[0]!.to} title={e.group} className={cn(rowBase, "justify-center px-0", active ? rowActive : rowIdle)}>
                    <span className="shrink-0 text-fg-3" aria-hidden>{e.icon}</span>
                  </NavLink>
                </li>
              );
            const open = !closedGroups.includes(e.group);
            const id = `nav-${mode}-${e.group.toLowerCase()}`;
            return (
              <li key={e.group}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={id}
                  onClick={() => setClosedGroups((g) => (open ? [...g, e.group] : g.filter((x) => x !== e.group)))}
                  className={cn(rowBase, "w-full", active && !open ? rowActive : rowIdle)}
                >
                  <span className="shrink-0 text-fg-3" aria-hidden>{e.icon}</span>
                  <span className="flex-1 text-left">{e.group}</span>
                  <ChevronDown className={cn("size-4 text-fg-3 transition-transform", !open && "-rotate-90")} aria-hidden />
                </button>
                {open && (
                  <ul id={id} className="mt-0.5 space-y-0.5">
                    {e.items.map((n) => (
                      <li key={n.to}>
                        <NavLink to={n.to} end={n.end} className={({ isActive }) => cn(rowBase, "pl-[42px]", isActive ? rowActive : rowIdle)}>
                          {n.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={cn("space-y-0.5 border-t border-border py-3", compact ? "px-2" : "px-3")}>
        <NotificationLink orgId={orgId} to={mode === "provider" ? "/notifications" : "/consumer/notifications"} compact={compact} />
        <FooterLink to="/docs" icon={<BookOpen className={ic} />} label="Documentation" compact={compact} />
        <CreditsLink orgId={orgId} compact={compact} />
        {me?.platformAdmin && <FooterLink to="/internal" icon={<Radar className={ic} />} label="Command centre" compact={compact} />}
        <AccountMenu compact={compact} />
      </div>
    </>
  );
}

function FooterLink({ to, icon, label, compact, trailing }: { to: string; icon: ReactNode; label: string; compact: boolean; trailing?: ReactNode }) {
  return (
    <NavLink to={to} title={compact ? label : undefined} className={({ isActive }) => cn(rowBase, compact && "relative justify-center px-0", isActive ? rowActive : rowIdle)}>
      <span className="shrink-0 text-fg-3" aria-hidden>{icon}</span>
      {!compact && <span className="flex-1">{label}</span>}
      {trailing}
    </NavLink>
  );
}

/** Wallet balance for the org (consumer credits). Hidden when the org has no wallet or the call is not permitted. */
function CreditsLink({ orgId, compact }: { orgId: string; compact: boolean }) {
  const [available, setAvailable] = useState<string | null>(null);
  const load = () => {
    api.org(orgId).consumer.billing().then((r) => setAvailable(r.balance?.available ?? null)).catch(() => setAvailable(null));
  };
  useEffect(load, [orgId]);
  useInterval(load, 60_000);
  // Billing announces a changed balance (e.g. credits just bought) so this updates at once.
  useEffect(() => {
    window.addEventListener("dbx:balance-changed", load);
    return () => window.removeEventListener("dbx:balance-changed", load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  return (
    <FooterLink
      to="/consumer/billing"
      icon={<Coins className={ic} />}
      label="Credits"
      compact={compact}
      trailing={!compact && available !== null ? <span className="tabular text-[13px] text-fg-3">{formatMoney(available, { precision: 2 })}</span> : undefined}
    />
  );
}

function WorkspaceSwitch({ mode, compact }: { mode: "provider" | "consumer"; compact: boolean }) {
  const nav = useNavigate();
  const options = [
    { key: "provider" as const, label: "Data provider", to: "/ask", swatch: "bg-accent" },
    { key: "consumer" as const, label: "Consumer", to: "/consumer", swatch: "bg-[#8b7cf6]" },
  ];
  const current = options.find((o) => o.key === mode)!;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={`Workspace: ${current.label}`}
        title={compact ? current.label : undefined}
        className={cn("flex h-10 w-full items-center gap-2.5 rounded-lg border border-border bg-panel text-[14px] text-fg hover:border-border-strong", compact ? "justify-center" : "px-3")}
      >
        <span className={cn("size-3.5 shrink-0 rounded-[4px]", current.swatch)} aria-hidden />
        {!compact && <span className="flex-1 text-left">{current.label}</span>}
        {!compact && <ChevronDown className="size-4 text-fg-3" aria-hidden />}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} className="z-50 min-w-56 rounded-lg border border-border-strong bg-panel-2 p-1 text-sm shadow-xl">
          <DropdownMenu.Label className="px-2.5 py-1.5 text-xs text-fg-3">Workspace</DropdownMenu.Label>
          {options.map((o) => (
            <DropdownMenu.Item key={o.key} onSelect={() => nav(o.to)} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-fg-2 outline-none data-[highlighted]:bg-panel-hover data-[highlighted]:text-fg">
              <span className={cn("size-3 rounded-[3px]", o.swatch)} aria-hidden />
              <span className="flex-1">{o.label}</span>
              {o.key === mode && <Check className="size-3.5 text-fg-3" aria-hidden />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** User + organisation card: switch org, settings, sign out. */
function AccountMenu({ compact }: { compact: boolean }) {
  const { me, membership, setOrgId, signOut } = useSession();
  const nav = useNavigate();
  if (!me || !membership) return null;
  const itemCls = "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-fg-2 outline-none data-[highlighted]:bg-panel-hover data-[highlighted]:text-fg";
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label="Account menu"
        className={cn("mt-1 flex w-full items-center gap-3 rounded-lg py-2 text-left hover:bg-panel-2/70", compact ? "justify-center px-0" : "px-2")}
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-panel text-fg-2" aria-hidden>
          <Building2 className="size-[18px] stroke-[1.6]" />
        </span>
        {!compact && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-medium text-fg">{me.user.name || me.user.email}</span>
            <span className="block truncate text-[12px] text-fg-3">{membership.orgName}</span>
          </span>
        )}
        {!compact && <ChevronDown className="size-4 text-fg-3" aria-hidden />}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content side="top" align="start" sideOffset={6} className="z-50 min-w-60 rounded-lg border border-border-strong bg-panel-2 p-1 text-sm shadow-xl">
          <div className="px-2.5 py-2 text-xs text-fg-3">{me.user.email}</div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-2.5 py-1 text-xs text-fg-3">Organisations</DropdownMenu.Label>
          {me.memberships.map((m) => (
            <DropdownMenu.Item key={m.orgId} onSelect={() => setOrgId(m.orgId)} className={itemCls}>
              <span className={cn("flex-1", m.orgId === membership.orgId && "text-fg")}>{m.orgName}</span>
              <span className="text-[10px] uppercase text-fg-3">{m.role.replace("_", " ")}</span>
              {m.orgId === membership.orgId && <Check className="size-3.5 text-fg-3" aria-hidden />}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item onSelect={() => nav("/settings")} className={itemCls}>
            <Settings className="size-3.5" /> Settings
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={async () => {
              await signOut();
              nav("/login");
            }}
            className={itemCls}
          >
            <LogOut className="size-3.5" /> Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Unread in-app notifications (demand matches, route losses, budget alerts, milestones). */
function NotificationLink({ orgId, to, compact }: { orgId: string; to: string; compact: boolean }) {
  const [unread, setUnread] = useState(0);
  const load = () => { gtmOrg(orgId).notifications().then((r) => setUnread(r.unread)).catch(() => {}); };
  useEffect(load, [orgId]);
  useInterval(load, 60_000);
  const badge = unread > 0 && (
    <span className={cn("tabular flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold text-accent-ink", compact && "absolute right-1 top-0.5 min-w-4 px-1 text-[10px]")}>
      {unread > 9 ? "9+" : unread}
    </span>
  );
  return <FooterLink to={to} icon={<Bell className={ic} />} label="Notifications" compact={compact} trailing={badge || undefined} />;
}
